// patternAnalysis.js — HelaBit-α Supreme Engine v10000%
// Multi-point regression, channel detection, Fibonacci, EMA trend, SMC/ICT, Harmonics

const getFutureOffset = (timeframe, periods = 5) => {
    const tf = timeframe ? timeframe.toString() : '15';
    let multiplier = 60;
    if (tf.includes('D') || tf === '1440') multiplier = 86400;
    else if (tf.includes('W') || tf === '10080') multiplier = 604800;
    else if (tf.includes('h') || tf === '60' || tf === '1h') multiplier = 3600;
    else if (tf === '4h' || tf === '240') multiplier = 14400;
    else multiplier = parseInt(tf) * 60 || 900;
    return multiplier * periods;
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const calcATR = (data, idx, period = 14) => {
    if (idx < period) return data[idx].high - data[idx].low;
    let sum = 0;
    for (let j = 0; j < period; j++) {
        const c = data[idx - j]; const p = data[idx - j - 1];
        if (!c || !p) continue;
        sum += Math.max(c.high - c.low, Math.abs(c.high - p.close), Math.abs(c.low - p.close));
    }
    return sum / period;
};

const calcEMA = (data, period) => {
    const k = 2 / (period + 1);
    let ema = data[0].close;
    for (let i = 1; i < data.length; i++) ema = data[i].close * k + ema * (1 - k);
    return ema;
};

// Linear regression across an array of {x, y} points
const linearRegression = (points) => {
    const n = points.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    for (const p of points) { sumX += p.x; sumY += p.y; sumXY += p.x * p.y; sumX2 += p.x * p.x; }
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    return { slope, intercept };
};

const findSwingPoints = (data, left = 5, right = 3) => {
    const highs = [], lows = [];
    for (let i = left; i < data.length - right; i++) {
        let isH = true, isL = true;
        for (let j = 1; j <= left; j++) {
            if (data[i - j].high >= data[i].high) isH = false;
            if (data[i - j].low <= data[i].low) isL = false;
        }
        for (let j = 1; j <= right; j++) {
            if (data[i + j].high >= data[i].high) isH = false;
            if (data[i + j].low <= data[i].low) isL = false;
        }
        if (isH) highs.push({ ...data[i], index: i });
        if (isL) lows.push({ ...data[i], index: i });
    }
    return { highs, lows };
};

// ─── Main Export ─────────────────────────────────────────────────────────────

export const detectPatterns = (data, timeframe) => {
    const markers = [];
    const trendlines = [];
    const verifiedTrendlines = [];
    const ictZones = [];
    let generatedLines = null;

    if (!data || data.length < 50) return { markers, lines: null, trendlines: [], ictZones: [], verifiedTrendlines: [] };

    const swings = findSwingPoints(data, 5, 3);
    const { highs: allHighs, lows: allLows } = { highs: swings.highs.slice(-25), lows: swings.lows.slice(-25) };
    const lastHighs = allHighs.slice(-5);
    const lastLows = allLows.slice(-5);
    const liveCandle = data[data.length - 1];
    const atr = calcATR(data, data.length - 1);
    const ema21 = calcEMA(data.slice(-60), 21);
    const ema50 = calcEMA(data.slice(-80), 50);
    const trendBias = liveCandle.close > ema50 ? 'BULL' : 'BEAR';

    const addMarker = (time, position, color, shape, text) =>
        markers.push({ time, position, color, shape, text, size: 1 });

    const candidates = [];

    // ══════════════════════════════════════════════════════════════════
    // 1. CLASSIC REVERSAL PATTERNS
    // ══════════════════════════════════════════════════════════════════

    // Head & Shoulders
    if (lastHighs.length >= 3) {
        const [p1, p2, p3] = [lastHighs[lastHighs.length - 3], lastHighs[lastHighs.length - 2], lastHighs[lastHighs.length - 1]];
        if (p2.high > p1.high && p2.high > p3.high && Math.abs(p1.high - p3.high) / p1.high < 0.03) {
            let t1 = { low: Infinity }, t2 = { low: Infinity };
            for (let k = p1.index; k < p2.index; k++) if (data[k].low < t1.low) { t1 = { ...data[k], index: k }; }
            for (let k = p2.index; k < p3.index; k++) if (data[k].low < t2.low) { t2 = { ...data[k], index: k }; }
            const avgNeck = (t1.low + t2.low) / 2;
            if (liveCandle.close < avgNeck) {
                const height = p2.high - avgNeck;
                generatedLines = {
                    pattern: 'Head & Shoulders', entry: avgNeck, stop: p3.high, target: avgNeck - height,
                    entryLine: [{ time: t1.time, value: avgNeck }, { time: liveCandle.time + getFutureOffset(timeframe, 10), value: avgNeck }],
                    stopLine: [{ time: p3.time, value: p3.high }, { time: liveCandle.time + getFutureOffset(timeframe, 10), value: p3.high }],
                    targetLine: [{ time: t1.time, value: avgNeck - height }, { time: liveCandle.time + getFutureOffset(timeframe, 10), value: avgNeck - height }]
                };
                candidates.push({
                    type: 'HEAD_SHOULDERS', priority: 100, startIdx: p1.index, endIdx: data.length - 1,
                    trendlines: [
                        { p1: { time: p1.time, value: p1.high }, p2: { time: t1.time, value: t1.low }, color: '#c084fc', lineStyle: 0 },
                        { p1: { time: t1.time, value: t1.low }, p2: { time: p2.time, value: p2.high }, color: '#c084fc', lineStyle: 0 },
                        { p1: { time: p2.time, value: p2.high }, p2: { time: t2.time, value: t2.low }, color: '#c084fc', lineStyle: 0 },
                        { p1: { time: t2.time, value: t2.low }, p2: { time: p3.time, value: p3.high }, color: '#c084fc', lineStyle: 0 },
                        { p1: { time: t1.time, value: t1.low }, p2: { time: liveCandle.time + getFutureOffset(timeframe, 5), value: t2.low }, color: 'rgba(251, 191, 36, 0.2)', title: 'NECKLINE', lineStyle: 2 }
                    ],
                    markers: [{ time: liveCandle.time, position: 'aboveBar', color: '#c084fc', shape: 'arrowDown', text: 'H&S BREAK' }]
                });
            }
        }
    }

    // Inverse H&S
    if (lastLows.length >= 3) {
        const [p1, p2, p3] = [lastLows[lastLows.length - 3], lastLows[lastLows.length - 2], lastLows[lastLows.length - 1]];
        if (p2.low < p1.low && p2.low < p3.low && Math.abs(p1.low - p3.low) / p1.low < 0.03) {
            let t1 = { high: -Infinity }, t2 = { high: -Infinity };
            for (let k = p1.index; k < p2.index; k++) if (data[k].high > t1.high) { t1 = { ...data[k], index: k }; }
            for (let k = p2.index; k < p3.index; k++) if (data[k].high > t2.high) { t2 = { ...data[k], index: k }; }
            const avgNeck = (t1.high + t2.high) / 2;
            if (liveCandle.close > avgNeck) {
                const height = avgNeck - p2.low;
                if (!generatedLines) generatedLines = {
                    pattern: 'Inv H&S', entry: avgNeck, stop: p3.low, target: avgNeck + height,
                    entryLine: [{ time: t1.time, value: avgNeck }, { time: liveCandle.time + getFutureOffset(timeframe, 10), value: avgNeck }],
                    stopLine: [{ time: p3.time, value: p3.low }, { time: liveCandle.time + getFutureOffset(timeframe, 10), value: p3.low }],
                    targetLine: [{ time: t1.time, value: avgNeck + height }, { time: liveCandle.time + getFutureOffset(timeframe, 10), value: avgNeck + height }]
                };
                candidates.push({
                    type: 'INV_HEAD_SHOULDERS', priority: 100, startIdx: p1.index, endIdx: data.length - 1,
                    trendlines: [
                        { p1: { time: p1.time, value: p1.low }, p2: { time: t1.time, value: t1.high }, color: '#34d399', lineStyle: 0 },
                        { p1: { time: t1.time, value: t1.high }, p2: { time: p2.time, value: p2.low }, color: '#34d399', lineStyle: 0 },
                        { p1: { time: p2.time, value: p2.low }, p2: { time: t2.time, value: t2.high }, color: '#34d399', lineStyle: 0 },
                        { p1: { time: t2.time, value: t2.high }, p2: { time: p3.time, value: p3.low }, color: '#34d399', lineStyle: 0 },
                        { p1: { time: t1.time, value: t1.high }, p2: { time: liveCandle.time + getFutureOffset(timeframe, 5), value: t2.high }, color: 'rgba(251, 191, 36, 0.2)', title: 'NECKLINE', lineStyle: 2 }
                    ],
                    markers: [{ time: liveCandle.time, position: 'belowBar', color: '#34d399', shape: 'arrowUp', text: 'INV H&S BREAK' }]
                });
            }
        }
    }

    // Double Top / Bottom
    if (lastHighs.length >= 2) {
        const [h1, h2] = [lastHighs[lastHighs.length - 2], lastHighs[lastHighs.length - 1]];
        let neckLow = Infinity, neckIdx = -1;
        for (let k = h1.index; k <= h2.index; k++) { if (data[k].low < neckLow) { neckLow = data[k].low; neckIdx = k; } }
        if (Math.abs(h1.high - h2.high) / h1.high < 0.015 && h2.index - h1.index > 5 && liveCandle.close < neckLow) {
            const height = h1.high - neckLow;
            if (!generatedLines) generatedLines = { pattern: 'Double Top (M)', entry: neckLow, stop: h2.high, target: neckLow - height };
            candidates.push({
                type: 'DOUBLE_TOP', priority: 85, startIdx: h1.index, endIdx: data.length - 1,
                trendlines: [
                    { p1: { time: h1.time, value: h1.high }, p2: { time: h2.time, value: h2.high }, color: '#f87171', title: 'DOUBLE TOP', lineStyle: 0 },
                    { p1: { time: data[neckIdx].time, value: neckLow }, p2: { time: liveCandle.time + getFutureOffset(timeframe, 8), value: neckLow }, color: 'rgba(251, 191, 36, 0.2)', title: 'NECKLINE', lineStyle: 2 }
                ],
                markers: [{ time: liveCandle.time, position: 'aboveBar', color: '#f87171', shape: 'arrowDown', text: 'DOUBLE TOP' }]
            });
        }
    }

    if (lastLows.length >= 2) {
        const [l1, l2] = [lastLows[lastLows.length - 2], lastLows[lastLows.length - 1]];
        let neckHigh = -Infinity, neckIdx = -1;
        for (let k = l1.index; k <= l2.index; k++) { if (data[k].high > neckHigh) { neckHigh = data[k].high; neckIdx = k; } }
        if (Math.abs(l1.low - l2.low) / l1.low < 0.015 && l2.index - l1.index > 5 && liveCandle.close > neckHigh) {
            if (!generatedLines) generatedLines = { pattern: 'Double Bottom (W)', entry: neckHigh, stop: l1.low, target: neckHigh + (neckHigh - l1.low) };
            candidates.push({
                type: 'DOUBLE_BOTTOM', priority: 85, startIdx: l1.index, endIdx: data.length - 1,
                trendlines: [
                    { p1: { time: l1.time, value: l1.low }, p2: { time: l2.time, value: l2.low }, color: '#34d399', title: 'DOUBLE BOTTOM', lineStyle: 0 },
                    { p1: { time: data[neckIdx].time, value: neckHigh }, p2: { time: liveCandle.time + getFutureOffset(timeframe, 8), value: neckHigh }, color: 'rgba(251, 191, 36, 0.2)', title: 'NECKLINE', lineStyle: 2 }
                ],
                markers: [{ time: liveCandle.time, position: 'belowBar', color: '#34d399', shape: 'arrowUp', text: 'DOUBLE BOTTOM' }]
            });
        }
    }

    // ══════════════════════════════════════════════════════════════════
    // 2. TRIANGLES & WEDGES
    // ══════════════════════════════════════════════════════════════════

    if (lastHighs.length >= 2 && lastLows.length >= 2) {
        const [h1, h2] = [lastHighs[lastHighs.length - 2], lastHighs[lastHighs.length - 1]];
        const [l1, l2] = [lastLows[lastLows.length - 2], lastLows[lastLows.length - 1]];

        // Ascending Triangle
        if (Math.abs(h1.high - h2.high) / h1.high < 0.006 && l2.low > l1.low * 1.004 && liveCandle.close > h2.high) {
            const height = h1.high - l1.low;
            if (!generatedLines) generatedLines = { pattern: 'Ascending Triangle', entry: h2.high, stop: l2.low, target: h2.high + height };
            candidates.push({
                type: 'ASC_TRIANGLE', priority: 70, startIdx: Math.min(l1.index, h1.index), endIdx: data.length - 1,
                trendlines: [
                    { p1: { time: h1.time, value: h1.high }, p2: { time: liveCandle.time + getFutureOffset(timeframe, 8), value: h1.high }, color: '#fbbf24', title: 'RESISTANCE', lineStyle: 0 },
                    { p1: { time: l1.time, value: l1.low }, p2: { time: l2.time, value: l2.low }, color: '#22d3ee', title: 'SUPPORT', lineStyle: 0 }
                ],
                markers: [{ time: liveCandle.time, position: 'belowBar', color: '#22d3ee', shape: 'arrowUp', text: 'ASC TRI ↑' }]
            });
        }

        // Descending Triangle
        else if (h2.high < h1.high * 0.996 && Math.abs(l1.low - l2.low) / l1.low < 0.006 && liveCandle.close < l2.low) {
            const height = h1.high - l1.low;
            if (!generatedLines) generatedLines = { pattern: 'Descending Triangle', entry: l2.low, stop: h2.high, target: l2.low - height };
            candidates.push({
                type: 'DESC_TRIANGLE', priority: 70, startIdx: Math.min(h1.index, l1.index), endIdx: data.length - 1,
                trendlines: [
                    { p1: { time: h1.time, value: h1.high }, p2: { time: h2.time, value: h2.high }, color: '#f87171', title: 'RESISTANCE', lineStyle: 0 },
                    { p1: { time: l1.time, value: l1.low }, p2: { time: liveCandle.time + getFutureOffset(timeframe, 8), value: l1.low }, color: '#fbbf24', title: 'SUPPORT', lineStyle: 0 }
                ],
                markers: [{ time: liveCandle.time, position: 'aboveBar', color: '#f87171', shape: 'arrowDown', text: 'DESC TRI ↓' }]
            });
        }

        // Symmetrical Triangle
        else if (h2.high < h1.high * 0.996 && l2.low > l1.low * 1.004) {
            const isUp = liveCandle.close > h2.high;
            const isDown = liveCandle.close < l2.low;
            candidates.push({
                type: 'SYM_TRIANGLE', priority: isUp || isDown ? 65 : 40, startIdx: Math.min(h1.index, l1.index), endIdx: data.length - 1,
                trendlines: [
                    { p1: { time: h1.time, value: h1.high }, p2: { time: h2.time, value: h2.high }, color: '#f87171', lineStyle: 0 },
                    { p1: { time: l1.time, value: l1.low }, p2: { time: l2.time, value: l2.low }, color: '#34d399', lineStyle: 0 }
                ],
                markers: isUp ? [{ time: liveCandle.time, position: 'belowBar', color: '#34d399', shape: 'arrowUp', text: 'SYM TRI ↑' }]
                    : isDown ? [{ time: liveCandle.time, position: 'aboveBar', color: '#f87171', shape: 'arrowDown', text: 'SYM TRI ↓' }] : []
            });
        }
    }

    // Wedges
    if (lastHighs.length >= 3 && lastLows.length >= 3) {
        const [h1, h2, h3] = [lastHighs[lastHighs.length - 3], lastHighs[lastHighs.length - 2], lastHighs[lastHighs.length - 1]];
        const [l1, l2, l3] = [lastLows[lastLows.length - 3], lastLows[lastLows.length - 2], lastLows[lastLows.length - 1]];

        // Rising Wedge (Bearish)
        if (h3.high > h2.high && h2.high > h1.high && l3.low > l2.low && l2.low > l1.low) {
            const hSlope = (h3.high - h1.high) / (h3.index - h1.index);
            const lSlope = (l3.low - l1.low) / (l3.index - l1.index);
            if (lSlope > hSlope * 1.15 && liveCandle.close < l3.low) {
                candidates.push({
                    type: 'RISING_WEDGE', priority: 78, startIdx: h1.index, endIdx: data.length - 1,
                    trendlines: [
                        { p1: { time: h1.time, value: h1.high }, p2: { time: h3.time, value: h3.high }, color: '#ff4466', title: 'RISING WEDGE', lineStyle: 0 },
                        { p1: { time: l1.time, value: l1.low }, p2: { time: l3.time, value: l3.low }, color: '#ff4466', lineStyle: 0 }
                    ],
                    markers: [{ time: liveCandle.time, position: 'aboveBar', color: '#ff4466', shape: 'arrowDown', text: 'RISING WEDGE ↓' }]
                });
            }
        }

        // Falling Wedge (Bullish)
        if (h3.high < h2.high && h2.high < h1.high && l3.low < l2.low && l2.low < l1.low) {
            const hSlope = Math.abs((h3.high - h1.high) / (h3.index - h1.index));
            const lSlope = Math.abs((l3.low - l1.low) / (l3.index - l1.index));
            if (hSlope > lSlope * 1.15 && liveCandle.close > h3.high) {
                candidates.push({
                    type: 'FALLING_WEDGE', priority: 78, startIdx: h1.index, endIdx: data.length - 1,
                    trendlines: [
                        { p1: { time: h1.time, value: h1.high }, p2: { time: h3.time, value: h3.high }, color: '#00ffaa', title: 'FALLING WEDGE', lineStyle: 0 },
                        { p1: { time: l1.time, value: l1.low }, p2: { time: l3.time, value: l3.low }, color: '#00ffaa', lineStyle: 0 }
                    ],
                    markers: [{ time: liveCandle.time, position: 'belowBar', color: '#00ffaa', shape: 'arrowUp', text: 'FALLING WEDGE ↑' }]
                });
            }
        }
    }

    // ══════════════════════════════════════════════════════════════════
    // 3. CHANNELS (Parallel Price Channels with Auto-Extension)
    // ══════════════════════════════════════════════════════════════════

    if (lastHighs.length >= 2 && lastLows.length >= 2) {
        const [h1, h2] = [lastHighs[lastHighs.length - 2], lastHighs[lastHighs.length - 1]];
        const [l1, l2] = [lastLows[lastLows.length - 2], lastLows[lastLows.length - 1]];
        const hSlope = (h2.high - h1.high) / (h2.index - h1.index);
        const lSlope = (l2.low - l1.low) / (l2.index - l1.index);

        if (Math.abs(hSlope - lSlope) / (Math.abs(hSlope) + 0.0001) < 0.18) {
            const isAsc = hSlope > 0 && lSlope > 0;
            const isDesc = hSlope < 0 && lSlope < 0;
            const color = isAsc ? '#22d3ee' : isDesc ? '#f87171' : '#a78bfa';
            const label = isAsc ? 'BULL CHANNEL' : isDesc ? 'BEAR CHANNEL' : 'FLAT CHANNEL';

            // Extend channel lines into future
            const futureOffset = getFutureOffset(timeframe, 15);
            const barsToFuture = 15;
            const h2ExtVal = h2.high + hSlope * barsToFuture;
            const l2ExtVal = l2.low + lSlope * barsToFuture;

            candidates.push({
                type: 'CHANNEL', priority: 68, startIdx: Math.min(h1.index, l1.index), endIdx: data.length - 1,
                trendlines: [
                    { p1: { time: h1.time, value: h1.high }, p2: { time: liveCandle.time + futureOffset, value: h2ExtVal }, color, title: label, lineStyle: 0 },
                    { p1: { time: l1.time, value: l1.low }, p2: { time: liveCandle.time + futureOffset, value: l2ExtVal }, color, lineStyle: 0 }
                ],
                markers: [{ time: liveCandle.time, position: isAsc ? 'belowBar' : 'aboveBar', color, shape: isAsc ? 'arrowUp' : 'arrowDown', text: label }]
            });
        }
    }

    // ══════════════════════════════════════════════════════════════════
    // 4. FLAGS & PENNANTS
    // ══════════════════════════════════════════════════════════════════

    if (data.length > 25) {
        const poleSize = 6;
        const flagSize = 12;
        const poleStart = data.length - flagSize - poleSize;
        const poleEnd = data.length - flagSize;
        if (poleStart >= 0) {
            const poleMove = ((data[poleEnd].close - data[poleStart].close) / data[poleStart].close) * 100;
            let maxFlag = -Infinity, minFlag = Infinity;
            for (let i = poleEnd; i < data.length; i++) { maxFlag = Math.max(maxFlag, data[i].high); minFlag = Math.min(minFlag, data[i].low); }
            const flagRange = maxFlag - minFlag;
            const poleHeight = Math.abs(data[poleEnd].close - data[poleStart].close);

            if (Math.abs(poleMove) > 2.5 && flagRange < poleHeight * 0.45) {
                const isBull = poleMove > 0;
                candidates.push({
                    type: isBull ? 'BULL_FLAG' : 'BEAR_FLAG', priority: 75, startIdx: poleStart, endIdx: data.length - 1,
                    trendlines: [
                        { p1: { time: data[poleStart].time, value: data[poleStart].close }, p2: { time: data[poleEnd].time, value: data[poleEnd].close }, color: isBull ? '#22d3ee' : '#f87171', title: isBull ? 'BULL FLAG POLE' : 'BEAR FLAG POLE', lineStyle: 0 }
                    ],
                    markers: [{ time: liveCandle.time, position: isBull ? 'belowBar' : 'aboveBar', color: isBull ? '#22d3ee' : '#f87171', shape: isBull ? 'arrowUp' : 'arrowDown', text: isBull ? 'BULL FLAG ↑' : 'BEAR FLAG ↓' }]
                });
            }
        }
    }

    // ══════════════════════════════════════════════════════════════════
    // 5. HARMONICS (Gartley, Bat, Butterfly, Crab)
    // ══════════════════════════════════════════════════════════════════

    if (allHighs.length >= 3 && allLows.length >= 3) {
        const x = allLows[allLows.length - 3];
        const a = allHighs[allHighs.length - 2];
        const b = allLows[allLows.length - 2];
        const c = allHighs[allHighs.length - 1];
        const d = allLows[allLows.length - 1];

        if (x && a && b && c && d && x.index < a.index && a.index < b.index && b.index < c.index && c.index < d.index) {
            const xa = a.high - x.low;
            const ab = a.high - b.low;
            const bc = c.high - b.low;
            const ab_xa = ab / xa;
            const bc_ab = bc / ab;

            let hType = null;
            if (Math.abs(ab_xa - 0.618) < 0.12 && bc_ab >= 0.38 && bc_ab <= 0.886) hType = 'GARTLEY';
            else if (Math.abs(ab_xa - 0.382) < 0.12 && bc_ab >= 0.382 && bc_ab <= 0.886) hType = 'BAT';
            else if (ab_xa >= 0.786 && ab_xa <= 0.886 && bc_ab >= 1.0) hType = 'BUTTERFLY';

            if (hType) {
                addMarker(d.time, 'belowBar', '#818cf8', 'arrowUp', hType);
                candidates.push({
                    type: `HARMONIC_${hType}`, priority: 90, startIdx: x.index, endIdx: d.index,
                    trendlines: [
                        { p1: { time: x.time, value: x.low }, p2: { time: a.time, value: a.high }, color: '#818cf8', lineStyle: 0 },
                        { p1: { time: a.time, value: a.high }, p2: { time: b.time, value: b.low }, color: '#818cf8', lineStyle: 0 },
                        { p1: { time: b.time, value: b.low }, p2: { time: c.time, value: c.high }, color: '#818cf8', lineStyle: 0 },
                        { p1: { time: c.time, value: c.high }, p2: { time: d.time, value: d.low }, color: '#818cf8', lineStyle: 0 }
                    ],
                    markers: [{ time: d.time, position: 'belowBar', color: '#818cf8', shape: 'arrowUp', text: `BULL ${hType}` }]
                });
            }
        }
    }

    // ══════════════════════════════════════════════════════════════════
    // 6. MULTI-POINT LINEAR REGRESSION TRENDLINES (The Main Upgrade)
    //    Connects ALL swing highs / swing lows via best-fit line
    //    and projects forward. Color-codes based on slope direction.
    // ══════════════════════════════════════════════════════════════════

    const drawRegressionTrendline = (points, isSupport, minPoints = 3) => {
        if (!points || points.length < minPoints) return;
        const recentPoints = points.slice(-8); // Use last 8 pivots max
        const pts = recentPoints.map((p, i) => ({ x: p.index, y: isSupport ? p.low : p.high }));
        const { slope, intercept } = linearRegression(pts);

        // R² quality check — only draw if regression is decent
        let ssTot = 0, ssRes = 0;
        const meanY = pts.reduce((s, p) => s + p.y, 0) / pts.length;
        for (const p of pts) { ssTot += (p.y - meanY) ** 2; ssRes += (p.y - (slope * p.x + intercept)) ** 2; }
        const r2 = 1 - ssRes / (ssTot + 0.0001);
        if (r2 < 0.70) return; // Only draw if R² > 70%

        const firstPt = recentPoints[0];
        const futureIdx = data.length - 1 + 20;
        const futureTime = liveCandle.time + getFutureOffset(timeframe, 20);

        const startVal = slope * firstPt.index + intercept;
        const endVal = slope * futureIdx + intercept;

        verifiedTrendlines.push({
            p1: { time: firstPt.time, value: startVal },
            p2: { time: futureTime, value: endVal },
            color: isSupport ? '#22c55e' : '#ef4444',
            strength: Math.round(r2 * 5),
            title: isSupport ? `REGRESSION SUP (R²${(r2 * 100).toFixed(0)}%)` : `REGRESSION RES (R²${(r2 * 100).toFixed(0)}%)`,
            r2
        });
    };

    // Draw regression lines from all recent swing highs and lows
    drawRegressionTrendline(allHighs, false, 3);
    drawRegressionTrendline(allLows, true, 3);

    // ══════════════════════════════════════════════════════════════════
    // 7. LIVE ANCHOR DIAGONAL TRENDLINES (Anchored to Most Extreme Pivot)
    // ══════════════════════════════════════════════════════════════════

    const drawAnchorLine = (points, isSupport) => {
        if (!points || points.length === 0) return;
        let anchor = points[0];
        for (const p of points) {
            if (isSupport ? p.low < anchor.low : p.high > anchor.high) anchor = p;
        }
        const anchorVal = isSupport ? anchor.low : anchor.high;
        const liveVal = isSupport ? liveCandle.low : liveCandle.high;
        if (anchor.time >= liveCandle.time) return;
        const barDiff = (data.length - 1) - anchor.index;
        if (barDiff <= 0) return;
        const slope = (liveVal - anchorVal) / barDiff;
        const futureBars = 25;
        const extVal = liveVal + slope * futureBars;

        verifiedTrendlines.push({
            p1: { time: anchor.time, value: anchorVal },
            p2: { time: liveCandle.time + getFutureOffset(timeframe, futureBars), value: extVal },
            color: isSupport ? '#10b981' : '#f43f5e',
            strength: 3,
            title: isSupport ? 'LIVE SUPPORT' : 'LIVE RESISTANCE'
        });
    };

    drawAnchorLine(allHighs, false);
    drawAnchorLine(allLows, true);

    // ══════════════════════════════════════════════════════════════════
    // 8. FIBONACCI RETRACEMENT ZONES (Automatic from Last Major Swing)
    // ══════════════════════════════════════════════════════════════════

    if (allHighs.length >= 1 && allLows.length >= 1) {
        const recentHigh = allHighs.reduce((a, b) => b.high > a.high ? b : a);
        const recentLow = allLows.reduce((a, b) => b.low < a.low ? b : a);
        const swingRange = recentHigh.high - recentLow.low;

        const fibLevels = [0.236, 0.382, 0.5, 0.618, 0.786];
        // Opacity reduced to 30% for cleaner chart
        const fibColors = ['rgba(100,116,139,0.3)', 'rgba(167,139,250,0.3)', 'rgba(245,158,11,0.3)', 'rgba(34,211,238,0.3)', 'rgba(244,63,94,0.3)'];
        const fibNames = ['FIB 23.6%', 'FIB 38.2%', 'FIB 50%', 'FIB 61.8%', 'FIB 78.6%'];

        const isBullRetracement = recentLow.index < recentHigh.index; // Low before High = Bull move
        const fibFrom = isBullRetracement ? recentHigh.high : recentLow.low;
        const fibDir = isBullRetracement ? -1 : 1; // Retrace direction

        fibLevels.forEach((level, i) => {
            const price = fibFrom + fibDir * swingRange * level;
            // Only plot fibs near current price (within 8% ATR band)
            if (Math.abs(price - liveCandle.close) < atr * 12) {
                verifiedTrendlines.push({
                    p1: { time: recentLow.time, value: price },
                    p2: { time: liveCandle.time + getFutureOffset(timeframe, 30), value: price },
                    color: fibColors[i],
                    strength: 2,
                    title: fibNames[i]
                });
            }
        });
    }

    // ══════════════════════════════════════════════════════════════════
    // 9. EMA LEVELS AS DYNAMIC PRICE LINES
    //    EMA 21 and EMA 50 drawn as horizontal reference levels
    // ══════════════════════════════════════════════════════════════════

    verifiedTrendlines.push({
        p1: { time: data[data.length - 40].time, value: ema21 },
        p2: { time: liveCandle.time + getFutureOffset(timeframe, 20), value: ema21 },
        color: 'rgba(245, 158, 11, 0.2)',
        strength: 2,
        title: 'EMA 21'
    });

    verifiedTrendlines.push({
        p1: { time: data[data.length - 60].time, value: ema50 },
        p2: { time: liveCandle.time + getFutureOffset(timeframe, 20), value: ema50 },
        color: 'rgba(167, 139, 250, 0.2)',
        strength: 2,
        title: 'EMA 50'
    });

    // ══════════════════════════════════════════════════════════════════
    // 10. ICT: FAIR VALUE GAPS (FVG) + ORDER BLOCKS (OB)
    // ══════════════════════════════════════════════════════════════════

    const FVG_WINDOW = Math.min(70, data.length - 2);
    for (let i = data.length - FVG_WINDOW; i < data.length - 1; i++) {
        if (i < 2) continue;
        const [c1, c2, c3] = [data[i - 2], data[i - 1], data[i]];

        // Bullish FVG
        if (c2.close > c2.open && c1.high < c3.low) {
            let mit = false;
            for (let j = i + 1; j < data.length; j++) { if (data[j].low < c1.high) { mit = true; break; } }
            if (!mit) ictZones.push({ type: 'FVG_BULLISH', label: 'FVG 📈', startTime: c2.time, top: c3.low, bottom: c1.high });
        }
        // Bearish FVG
        if (c2.close < c2.open && c1.low > c3.high) {
            let mit = false;
            for (let j = i + 1; j < data.length; j++) { if (data[j].high > c1.low) { mit = true; break; } }
            if (!mit) ictZones.push({ type: 'FVG_BEARISH', label: 'FVG 📉', startTime: c2.time, top: c1.low, bottom: c3.high });
        }
    }

    // Order Blocks (enhanced — only unmitigated)
    const OB_WINDOW = Math.min(50, data.length - 3);
    for (let i = data.length - OB_WINDOW; i < data.length - 2; i++) {
        const [cur, nx1, nx2] = [data[i], data[i + 1], data[i + 2]];
        // Bullish OB — last bearish candle before strong bull push
        if (cur.close < cur.open && nx1.close > nx1.open && nx2.close > nx2.open) {
            const impulse = (nx2.close - cur.low) / cur.low;
            if (impulse > 0.008) {
                let mit = false;
                for (let j = i + 3; j < data.length; j++) { if (data[j].low < cur.low) { mit = true; break; } }
                if (!mit) ictZones.push({ type: 'OB_BULLISH', label: 'Bull OB', color: 'rgba(16,185,129,0.15)', startTime: cur.time, top: cur.high, bottom: cur.low });
            }
        }
        // Bearish OB — last bullish candle before strong bear push
        if (cur.close > cur.open && nx1.close < nx1.open && nx2.close < nx2.open) {
            const impulse = (cur.high - nx2.close) / nx2.close;
            if (impulse > 0.008) {
                let mit = false;
                for (let j = i + 3; j < data.length; j++) { if (data[j].high > cur.high) { mit = true; break; } }
                if (!mit) ictZones.push({ type: 'OB_BEARISH', label: 'Bear OB', color: 'rgba(239,68,68,0.15)', startTime: cur.time, top: cur.high, bottom: cur.low });
            }
        }
    }

    // ══════════════════════════════════════════════════════════════════
    // 11. SMC: BREAK OF STRUCTURE + CHANGE OF CHARACTER
    // ══════════════════════════════════════════════════════════════════

    if (allHighs.length >= 2 && allLows.length >= 2) {
        const lastHigh = allHighs[allHighs.length - 1];
        const prevHigh = allHighs[allHighs.length - 2];
        const lastLow = allLows[allLows.length - 1];
        const prevLow = allLows[allLows.length - 2];

        // Bullish BoS
        if (liveCandle.close > lastHigh.high && trendBias === 'BULL') {
            addMarker(liveCandle.time, 'aboveBar', '#22d3ee', 'arrowUp', 'BoS ↑');
            verifiedTrendlines.push({
                p1: { time: lastHigh.time, value: lastHigh.high },
                p2: { time: liveCandle.time + getFutureOffset(timeframe, 10), value: lastHigh.high },
                color: '#22d3ee', strength: 4, title: 'BoS LEVEL ↑'
            });
        }
        // Bearish BoS
        if (liveCandle.close < lastLow.low && trendBias === 'BEAR') {
            addMarker(liveCandle.time, 'belowBar', '#f43f5e', 'arrowDown', 'BoS ↓');
            verifiedTrendlines.push({
                p1: { time: lastLow.time, value: lastLow.low },
                p2: { time: liveCandle.time + getFutureOffset(timeframe, 10), value: lastLow.low },
                color: '#f43f5e', strength: 4, title: 'BoS LEVEL ↓'
            });
        }
        // CHoCH — Change of Character (trend reversal signal)
        if (trendBias === 'BULL' && liveCandle.close < prevLow.low) {
            addMarker(liveCandle.time, 'belowBar', '#f59e0b', 'circle', 'CHoCH ⚡');
        }
        if (trendBias === 'BEAR' && liveCandle.close > prevHigh.high) {
            addMarker(liveCandle.time, 'aboveBar', '#f59e0b', 'circle', 'CHoCH ⚡');
        }
    }

    // ══════════════════════════════════════════════════════════════════
    // 12. PRIORITY DEDUPLICATION
    // ══════════════════════════════════════════════════════════════════

    candidates.sort((a, b) => b.priority !== a.priority ? b.priority - a.priority : b.startIdx - a.startIdx);

    const occupied = [];
    for (const cand of candidates) {
        let overlaps = false;
        for (const [s, e] of occupied) {
            const startMax = Math.max(cand.startIdx, s);
            const endMin = Math.min(cand.endIdx, e);
            if (endMin > startMax && (endMin - startMax) > (cand.endIdx - cand.startIdx) * 0.3) {
                overlaps = true; break;
            }
        }
        if (!overlaps) {
            if (cand.markers) markers.push(...cand.markers);
            if (cand.trendlines) trendlines.push(...cand.trendlines);
            if (!generatedLines && cand.lines) generatedLines = cand.lines;
            occupied.push([cand.startIdx, cand.endIdx]);
        }
    }

    // ══════════════════════════════════════════════════════════════════
    // 13. ELITE CANDLESTICK PATTERN MARKERS (Last 80 Bars)
    // ══════════════════════════════════════════════════════════════════

    const start50 = Math.max(2, data.length - 80);
    for (let i = start50; i < data.length; i++) {
        const c3 = data[i], c2 = data[i - 1], c1 = data[i - 2];
        if (!c1 || !c2 || !c3) continue;
        const body3 = Math.abs(c3.close - c3.open);
        const body2 = Math.abs(c2.close - c2.open);
        const range3 = c3.high - c3.low;
        const isBull3 = c3.close > c3.open;
        const isBull2 = c2.close > c2.open;
        const upW3 = c3.high - Math.max(c3.open, c3.close);
        const loW3 = Math.min(c3.open, c3.close) - c3.low;
        const atrI = calcATR(data, i);
        const isDoji = range3 > 0 && body3 / range3 < 0.1;

        if (loW3 > body3 * 2 && upW3 < range3 * 0.1) addMarker(c3.time, 'belowBar', isBull3 ? '#22c55e' : '#eab308', 'arrowUp', '');
        if (upW3 > body3 * 2 && loW3 < range3 * 0.1) addMarker(c3.time, 'aboveBar', !isBull3 ? '#ef4444' : '#eab308', 'arrowDown', '');
        if (body3 > range3 * 0.9 && body3 > atrI) addMarker(c3.time, isBull3 ? 'belowBar' : 'aboveBar', isBull3 ? '#22c55e' : '#ef4444', 'square', '');
        if (isDoji) addMarker(c3.time, 'aboveBar', '#94a3b8', 'circle', '');
        if (isBull3 && !isBull2 && c3.close > c2.open && c3.open < c2.close && body3 > body2) addMarker(c3.time, 'belowBar', '#22c55e', 'arrowUp', '');
        else if (!isBull3 && isBull2 && c3.close < c2.open && c3.open > c2.close && body3 > body2) addMarker(c3.time, 'aboveBar', '#ef4444', 'arrowDown', '');
        if (body3 > atrI * 1.8 && isBull3) addMarker(c3.time, 'belowBar', '#22c55e', 'arrowUp', '');
        if (body3 > atrI * 1.8 && !isBull3) addMarker(c3.time, 'aboveBar', '#ef4444', 'arrowDown', '');
    }

    // ══════════════════════════════════════════════════════════════════
    // 14. DEFAULT FALLBACK (Market Structure Lines)
    // ══════════════════════════════════════════════════════════════════

    if (!generatedLines) {
        const risk = calcATR(data, data.length - 1) * 1.5;
        generatedLines = {
            pattern: `Market Structure (${trendBias})`,
            entry: liveCandle.close,
            stop: liveCandle.low - risk,
            target: liveCandle.high + risk * 2,
        };
    }

    markers.sort((a, b) => a.time - b.time);

    // Cap ICT zones to most relevant 8
    const cappedZones = ictZones.slice(-8);

    return { markers, lines: generatedLines, trendlines, ictZones: cappedZones, verifiedTrendlines };
};
